import React, {
    useState,
    useEffect,
    forwardRef,
    useImperativeHandle,
    useRef,
} from "react";
import Button from "@mui/material/Button";
import GetAllChatsFromCosmos from "../../../../utils/database/GetAllChatsFromCosmos";
import CreateChat from "../../../../utils/database/CreateChat";
import DeleteChat from "../../../../utils/database/DeleteChat";
import UpdateChatName from "../../../../utils/database/UpdateChatName";
import UserDetails from "../../../../utils/auth/UserDetails";
import ChatItem from "./ChatItem";
import Loader from "../../../general/Loader";
import "./Chats.css";
import { TextField, Tooltip, CircularProgress } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

const Chats = forwardRef((props, ref) => {
    const {
        onChatSelect,
        selectedChatId,
        onChatsUpdate,
        setChatId,
        onShowBanner,
        oldChats,
        setOldChats,
        setSelectedChatObject,
        isCreatingChat,
        setIsCreatingChat
    } = props;

    const [loading, setLoading] = useState(true);
    const [chatsLoaded, setChatsLoaded] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const inputRef = useRef(null);
    const userDetails = UserDetails();


    const handleCreateNewChat = async (extensionId = "DONA") => {
        setIsCreatingChat(true);
        try {
            const response = await CreateChat(userDetails.userId, extensionId);
            if (response && response.chatId) {
                setOldChats((prevChats) => [response, ...prevChats]);
                setChatId(response.chatId);
                onChatSelect(response);
                onChatsUpdate();
            }
        } catch (error) {
            console.error("Error creating chat:", error);
        }
        setIsCreatingChat(false);
    };


    useEffect(() => {
        const loadChats = async () => {
            if (!userDetails || chatsLoaded) return;

            setLoading(true);
            try {
                const chats = await GetAllChatsFromCosmos(userDetails.userId);
                const sortedChats = chats.sort(
                    (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
                );

                setOldChats(sortedChats);
                if(sortedChats.length === 0) {
                    console.log("Creating new chat");
                    
                    handleCreateNewChat();
                }
            
                setChatsLoaded(true);
            } catch (error) {
                console.error("Error loading chats:", error);
            }
            setLoading(false);
        };

        loadChats();
    }, [userDetails, chatsLoaded]);

    const groupChatsByDate = (chats) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);

        const todayChats = [];
        const yesterdayChats = [];
        const lastWeekChats = [];
        const lastMonthChats = [];
        const earlierChats = [];

        chats.forEach(chat => {
            const chatDate = new Date(chat.timestamp);
            chatDate.setHours(0, 0, 0, 0);

            const timeDiff = today.getTime() - chatDate.getTime();
            const dayDiff = Math.floor(timeDiff / (1000 * 3600 * 24));

            if (dayDiff === 0) {
                todayChats.push(chat);
            } else if (dayDiff === 1) {
                yesterdayChats.push(chat);
            } else if (dayDiff >= 2 && dayDiff <= 7) {
                lastWeekChats.push(chat);
            } else if (dayDiff >= 8 && dayDiff <= 30) {
                lastMonthChats.push(chat);
            } else {
                earlierChats.push(chat);
            }
        });

        return { todayChats, yesterdayChats, lastWeekChats, lastMonthChats, earlierChats };
    };

  

 
    const handleChatClick = (chat) => {
        onChatSelect(chat);
    };

    const handleRenameChat = async (chatId, newChatName, isManuallyRenamed = false) => {
        const status = await UpdateChatName(chatId, userDetails.userId, newChatName, isManuallyRenamed);
        if (status) {
            setOldChats((prevChats) =>
                prevChats.map((c) =>
                    c.chatId === chatId ? { ...c, chatName: newChatName, isManuallyRenamed: isManuallyRenamed } : c
                )
            );
        }
        onChatsUpdate();
    };

    const handleDeleteChat = async (chatId) => {
        const status = await DeleteChat(chatId, userDetails.userId);
        if (status) {
            setSelectedChatObject(null);
            setOldChats((prevChats) => prevChats.filter((c) => c.chatId !== chatId));
            if (chatId === selectedChatId) {
                
                onShowBanner();
             
            }
        }
        onChatsUpdate();
    };

    useImperativeHandle(ref, () => ({
        createNewChat: handleCreateNewChat
    }));

    const filteredChats = oldChats.filter(chat =>
        chat.chatName.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // const handleSearchClick = () => {
    //     setIsSearchOpen(true);
    //     setTimeout(() => inputRef.current?.focus(), 100);
    // };

    const handleCloseSearch = () => {
        setIsSearchOpen(false);
        setSearchQuery("");
    };

    return (
        <div className="chats-section">
            {loading ? (
                <Loader />
            ) : (
                <>
                    <div className="icons-div">
                        <Tooltip title="New Chat">
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => handleCreateNewChat("DONA")}
                                disabled={isCreatingChat}
                                sx={{
                                    width: "95%",
                                    padding: "8px 24px",
                                    backgroundColor: isCreatingChat ? "#4F4F8A" : "#333366",
                                    color: "#FFFFFF",
                                    "&:hover": {
                                        backgroundColor: "#4F4F8A",
                                    },
                                    borderRadius: "8px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    position: "relative",
                                    gap: "8px", // Ensures spacing between text and loader
                                }}
                            >
                                {isCreatingChat && <CircularProgress size={18} sx={{ color: "#FFFFFF" }} />}
                                {isCreatingChat ? "Creating..." : "New Chat"}
                            </Button>
                        </Tooltip>


                        <div className="search-div" style={{ flex: isSearchOpen ? 1 : "unset" }}>
                            <TextField
                                inputRef={inputRef}
                                variant="outlined"
                                placeholder="Search chats..."
                                fullWidth
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={(e) => e.key === "Escape" && handleCloseSearch()}
                                InputProps={{
                                    startAdornment: (
                                        <SearchIcon style={{ marginRight: "8px", cursor: "pointer" }} />
                                    ),
                                    sx: {
                                        py: 0.5, // Increases vertical padding inside the input
                                    },
                                }}
                                sx={{
                                    width: "95%",
                                    marginTop: "16px",
                                    borderRadius: "8px",
                                    display: "flex",
                                    justifyContent: "center",
                                    position: "relative",
                                    "& .MuiOutlinedInput-root": {
                                        "& fieldset": {
                                            borderColor: "#4F4F8A", // Default border color (your tertiary purple)
                                        },
                                        "&:hover fieldset": {
                                            borderColor: "#4527a0", // Darker purple on hover
                                        },
                                        "&.Mui-focused fieldset": {
                                            borderColor: "#4527a0", // Darker purple on focus
                                        },
                                    },
                                }}
                                margin="dense"
                            />

                        </div>
                    </div>

                    {filteredChats.length === 0 ? (
                        <div className="no-chats-message">
                            <p>No chats found.</p>
                        </div>
                    ) : (
                        <div className="old-chats">
                            {(() => {
                                const { todayChats, yesterdayChats, lastWeekChats, lastMonthChats, earlierChats } = groupChatsByDate(filteredChats);
                                return (
                                    <>
                                        {todayChats.length > 0 && (
                                            <div className="today">
                                                <p className="chat-group-title">Today</p>
                                                {todayChats.map((chat, index) => (
                                                    <ChatItem
                                                        key={index}
                                                        chat={chat}
                                                        selectedChatId={selectedChatId}
                                                        onChatSelect={handleChatClick}
                                                        onRename={handleRenameChat}
                                                        onDelete={handleDeleteChat}
                                                    />
                                                ))}
                                            </div>
                                        )}

                                        {yesterdayChats.length > 0 && (
                                            <>
                                                <p className="chat-group-title">Yesterday</p>
                                                {yesterdayChats.map((chat, index) => (
                                                    <ChatItem
                                                        key={index}
                                                        chat={chat}
                                                        selectedChatId={selectedChatId}
                                                        onChatSelect={handleChatClick}
                                                        onRename={handleRenameChat}
                                                        onDelete={handleDeleteChat}
                                                    />
                                                ))}
                                            </>
                                        )}

                                        {lastWeekChats.length > 0 && (
                                            <>
                                                <p className="chat-group-title">Last Week</p>
                                                {lastWeekChats.map((chat, index) => (
                                                    <ChatItem
                                                        key={index}
                                                        chat={chat}
                                                        selectedChatId={selectedChatId}
                                                        onChatSelect={handleChatClick}
                                                        onRename={handleRenameChat}
                                                        onDelete={handleDeleteChat}
                                                    />
                                                ))}
                                            </>
                                        )}

                                        {lastMonthChats.length > 0 && (
                                            <>
                                                <p className="chat-group-title">Last Month</p>
                                                {lastMonthChats.map((chat, index) => (
                                                    <ChatItem
                                                        key={index}
                                                        chat={chat}
                                                        selectedChatId={selectedChatId}
                                                        onChatSelect={handleChatClick}
                                                        onRename={handleRenameChat}
                                                        onDelete={handleDeleteChat}
                                                    />
                                                ))}
                                            </>
                                        )}

                                        {earlierChats.length > 0 && (
                                            <>
                                                <p className="chat-group-title">Earlier</p>
                                                {earlierChats.map((chat, index) => (
                                                    <ChatItem
                                                        key={index}
                                                        chat={chat}
                                                        selectedChatId={selectedChatId}
                                                        onChatSelect={handleChatClick}
                                                        onRename={handleRenameChat}
                                                        onDelete={handleDeleteChat}
                                                    />
                                                ))}
                                            </>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                    )}
                </>
            )}
        </div>
    );
});

export default Chats;